import { useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { rfiApi, tecniciApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import { Plus, AlertTriangle, Upload, Trash2 } from 'lucide-react'

interface RfiOrdine {
  id: number
  oggetto: string
  mittente_email?: string
  urgente: boolean
  stato: string
  tecnici_ids: number[]
  rapportino_id?: number
}

interface TecnicoOption {
  id: number
  nome: string
  cognome: string
}

function BadgeStatoOdl({ stato }: { stato: string }) {
  const stile: Record<string, string> = {
    nuovo: 'bg-slate-100 text-slate-600',
    assegnato: 'bg-amber-100 text-amber-700',
    in_corso: 'bg-blue-100 text-blue-700',
    chiuso: 'bg-green-100 text-green-700',
  }
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${stile[stato] || stile.nuovo}`}>{stato}</span>
}

function NuovoOrdineModal({ onClose, tecnici }: { onClose: () => void; tecnici: TecnicoOption[] }) {
  const qc = useQueryClient()
  const [oggetto, setOggetto] = useState('')
  const [mittenteEmail, setMittenteEmail] = useState('')
  const [corpoTesto, setCorpoTesto] = useState('')
  const [urgente, setUrgente] = useState(false)
  const [tecniciIds, setTecniciIds] = useState<number[]>([])

  const creaMut = useMutation({
    mutationFn: () => rfiApi.creaOrdine({ oggetto, mittente_email: mittenteEmail || undefined, corpo_testo: corpoTesto || undefined, urgente, tecnici_ids: tecniciIds }),
    onSuccess: () => {
      toast.success('Ordine di lavoro creato')
      qc.invalidateQueries({ queryKey: ['rfi-ordini'] })
      onClose()
    },
    onError: () => toast.error('Errore nella creazione'),
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="card p-6 w-full max-w-lg space-y-3">
        <h2 className="text-lg font-semibold">Nuovo Ordine di Lavoro</h2>
        <div>
          <label className="label">Oggetto</label>
          <input className="input w-full" value={oggetto} onChange={e => setOggetto(e.target.value)} />
        </div>
        <div>
          <label className="label">Mittente email</label>
          <input className="input w-full" value={mittenteEmail} onChange={e => setMittenteEmail(e.target.value)} />
        </div>
        <div>
          <label className="label">Testo email</label>
          <textarea className="input w-full" rows={4} value={corpoTesto} onChange={e => setCorpoTesto(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={urgente} onChange={e => setUrgente(e.target.checked)} /> Urgente
        </label>
        <div>
          <label className="label">Tecnici</label>
          <select
            multiple className="input w-full h-32"
            value={tecniciIds.map(String)}
            onChange={e => setTecniciIds(Array.from(e.target.selectedOptions).map(o => Number(o.value)))}
          >
            {tecnici.map(t => (
              <option key={t.id} value={t.id}>{t.nome} {t.cognome}</option>
            ))}
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-ghost" onClick={onClose}>Annulla</button>
          <button className="btn-primary" disabled={!oggetto || creaMut.isPending} onClick={() => creaMut.mutate()}>
            {creaMut.isPending ? 'Creazione...' : 'Crea'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function RfiOrdini() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user } = useAuth()
  const isAdmin = ['admin', 'superadmin'].includes(user?.role ?? '')
  const [showNuovo, setShowNuovo] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const importaListinoMut = useMutation({
    mutationFn: (file: File) => rfiApi.importaListino(file),
    onSuccess: () => toast.success('Import avviato in background'),
    onError: () => toast.error('Errore avvio import listino'),
  })

  const onFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) importaListinoMut.mutate(file)
    e.target.value = ''
  }

  const { data: ordini = [], isLoading } = useQuery({
    queryKey: ['rfi-ordini'],
    queryFn: () => rfiApi.listaOrdini().then(r => r.data as RfiOrdine[]),
  })

  const { data: tecnici = [] } = useQuery({
    queryKey: ['tecnici-lista-rfi'],
    queryFn: () => tecniciApi.list({ per_page: 100 }).then((r: any) => (r.data.items || r.data) as TecnicoOption[]),
  })

  const avviaMut = useMutation({
    mutationFn: ({ id, tecniciIds }: { id: number; tecniciIds: number[] }) => rfiApi.assegnaOrdine(id, tecniciIds),
    onSuccess: (r) => {
      toast.success('Attività avviata')
      qc.invalidateQueries({ queryKey: ['rfi-ordini'] })
      navigate(`/rfi/ordini/${r.data.id}`)
    },
    onError: () => toast.error('Errore avvio attività'),
  })

  const eliminaMut = useMutation({
    mutationFn: (id: number) => rfiApi.eliminaOrdine(id),
    onSuccess: () => {
      toast.success('Ordine di lavoro eliminato')
      qc.invalidateQueries({ queryKey: ['rfi-ordini'] })
    },
    onError: () => toast.error('Errore eliminazione'),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">RFI — Ordini di Lavoro</h1>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <input
                ref={fileInputRef} type="file" accept=".xlsx" className="hidden"
                onChange={onFileSelected}
              />
              <button
                className="btn-secondary flex items-center gap-1.5"
                disabled={importaListinoMut.isPending}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={16} /> Importa Listino
              </button>
            </>
          )}
          <button className="btn-primary flex items-center gap-1.5" onClick={() => setShowNuovo(true)}>
            <Plus size={16} /> Nuovo Ordine di Lavoro
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-slate-400">Caricamento...</div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b">
              <th className="text-left px-4 py-2 text-xs text-slate-500">Oggetto</th>
              <th className="text-left px-4 py-2 text-xs text-slate-500">Stato</th>
              <th className="text-left px-4 py-2 text-xs text-slate-500">Urgente</th>
              <th className="text-center px-4 py-2 text-xs text-slate-500">Azioni</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-50">
              {ordini.map(o => (
                <tr key={o.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => o.rapportino_id && navigate(`/rfi/ordini/${o.id}`)}>
                  <td className="px-4 py-2">{o.oggetto}</td>
                  <td className="px-4 py-2"><BadgeStatoOdl stato={o.stato} /></td>
                  <td className="px-4 py-2">{o.urgente && <AlertTriangle size={14} className="text-red-500" />}</td>
                  <td className="px-4 py-2 text-center" onClick={e => e.stopPropagation()}>
                    {o.stato === 'nuovo' && o.tecnici_ids.length > 0 && (
                      <button
                        className="btn-secondary text-xs py-1"
                        disabled={avviaMut.isPending}
                        onClick={() => avviaMut.mutate({ id: o.id, tecniciIds: o.tecnici_ids })}
                      >
                        Avvia attività
                      </button>
                    )}
                    {o.stato === 'nuovo' && o.tecnici_ids.length === 0 && (
                      <span className="text-xs text-slate-400" title="Nessun tecnico assegnato alla creazione">
                        Assegna un tecnico
                      </span>
                    )}
                    {o.rapportino_id && (
                      <button className="btn-ghost text-xs py-1" onClick={() => navigate(`/rfi/ordini/${o.id}`)}>Apri</button>
                    )}
                    {isAdmin && (
                      <button
                        className="btn-ghost text-xs py-1 text-red-600"
                        disabled={eliminaMut.isPending}
                        onClick={() => { if (confirm(`Eliminare l'ordine di lavoro "${o.oggetto}"? L'operazione non è reversibile.`)) eliminaMut.mutate(o.id) }}
                      >
                        <Trash2 size={12} className="inline" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNuovo && <NuovoOrdineModal onClose={() => setShowNuovo(false)} tecnici={tecnici} />}
    </div>
  )
}
